import axios from 'axios';
import { config } from '../config';

// User data key (token is now in HttpOnly cookie)
const USER_KEY = 'cartcritic_user';

const api = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true, // Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'web', // Tell backend this is a web client
  },
});

// Silent refresh interceptor — on 401, attempt token refresh before redirecting
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints — let callers handle the error directly
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null);
        return api(originalRequest); // Retry with new cookie
      } catch (refreshError) {
        processQueue(refreshError);
        sessionStorage.removeItem(USER_KEY);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
